import axios from 'axios';
import { responseCodes, responseMessages } from 'src/translations/serverMessages';
import { signout } from 'src/contexts/JWTAuthContext';

const baseRoute = '/';

export function fetchCourseFile(path: string): Promise<Response> {
  const token = localStorage.getItem('accessToken');
  return fetch(`${baseRoute}v2/course/content/${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
}

export interface ApiResponse<T> {
    success: boolean;
    message?: string;
    data?: T;
}

export async function loadCourseData<T>(file: string): Promise<T> {
  const response = await fetchCourseFile(`${file}.json`);
  if (!response.ok) throw new Error(`Failed to load ${file}`);
  return response.json();
}

export async function fetchMarkdownModules(
  files: string[], 
  userLanguage: string,
  isSubmodule: boolean = false
): Promise<Record<string, string>> {
  const entries = await Promise.all(
    files.map(async (file) => {
      const response = await fetch(`/content/translations/${isSubmodule ? 'submodules' : 'modules'}/${file}_${userLanguage}.md`);
      const text = response.ok ? await response.text() : "";
      return [file, text] as [string, string];
    })
  );
  return Object.fromEntries(entries);
}



export async function handleApiCall<T>(
    method: string,
    route: string,
    data?: {query?: string; body?: any}
): Promise<ApiResponse<T>> {
    const apiClient = axios.create({
        baseURL: baseRoute,
    });
    
    apiClient.interceptors.request.use((config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    try {
        let path = baseRoute + route;

        if (data && data.query) {
            path += data.query;
        }

        const response = await apiClient[method](path, data?.body);
        const prefix = responseCodes[method + ':' + route];
        const tag = responseMessages.en[prefix + '200'];
        const message = (tag ? prefix : 'i') + '200';
       
        return { success: true, message, data: response.data };
    } catch (error) {
        console.log(method  + ' ' + route + ' failed: ' + error.message);

        if (error.message === 'Network Error') {
             return { success: false, message: 'net' };
        }

        if (error.response) {
            if (error.response.status === 500) {
                return { success: false, message: error.response.data.message }
            }

            if (error.response.status === 401 &&
                route !== 'v2/public/auth' &&
                route !== 'v2/public/reset') {
                signout()
                return { success: false, message: 'i401' };
            }
            if (error.response.status === 400) {
                return { success: false, message: 'i400' };
            }
            if (error.response.status === 404) {
                //signout();
                return { success: false, message: 'i404' };
            }
        }

        const errorCode = error.response.status.toString();
        const prefix = responseCodes[method + ':' + route];
        const tag = responseMessages.en[prefix + errorCode];
        const message = (tag ? prefix : 'i') + errorCode;

        return { success: false, message };
    }
}

export const setSession = (accessToken) => {
    if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
        localStorage.removeItem('accessToken')
        delete axios.defaults.headers.common.Authorization;
    }
}
